import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Label,
    ModalDialog,
    SelectFilled,
    type OptionProps,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { FormEvent } from 'react';

import { useAmelioTranslations } from '@/hooks';

type MoveCompetencyModalProps = {
    /** The page mounts this dialog only while a competency is being moved, so it opens by default. */
    isOpen?: boolean;
    competencyName: string;
    groupOptions: OptionProps[];
    isSubmitting: boolean;
    onConfirm: (targetCompetencyGroupId: string) => void;
    onCancel: () => void;
};

export const MoveCompetencyModal = ({
    isOpen = true,
    competencyName,
    groupOptions,
    isSubmitting,
    onConfirm,
    onCancel,
}: MoveCompetencyModalProps) => {
    const { t } = useAmelioTranslations();

    const [targetGroupId, setTargetGroupId] = useState<string>('');

    const selectOptions = useMemo(
        () =>
            groupOptions.map((option) => ({
                ...option,
                value: String(option.value ?? option.id),
            })),
        [groupOptions],
    );

    const canSubmit = Boolean(targetGroupId) && !isSubmitting;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (canSubmit) {
            onConfirm(targetGroupId);
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onCancel}
            closeAriaLabel={t('common.close')}
            title={t('competencies.moveCompetency.title', { name: competencyName })}
            size={'md'}
            onSubmit={handleSubmit}
            backdropBlur
            dataTestId={'competency-move-modal'}
            className={'[&_h3]:text-[18px]'}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        size={'md'}
                        onClick={onCancel}
                        dataTestId={'competency-move-cancel'}
                        fitContent
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        type={'submit'}
                        size={'md'}
                        disabled={!canSubmit}
                        dataTestId={'competency-move-confirm'}
                        fitContent
                    >
                        {t('competencies.moveCompetency.confirm')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-2'}>
                <Label required>{t('competencies.moveCompetency.targetLabel')}</Label>

                <SelectFilled
                    value={targetGroupId}
                    options={selectOptions}
                    onChange={(value) => setTargetGroupId(value)}
                    placeholder={t('competencies.moveCompetency.targetPlaceholder')}
                    emptyText={t('common.noResults')}
                    ariaLabel={t('competencies.moveCompetency.targetLabel')}
                    className={'border border-gray-200 bg-white'}
                    sort={'none'}
                    dataTestId={'competency-move-target-select'}
                />
            </div>
        </ModalDialog>
    );
};
